import React from 'react';
import { Guide } from 'types/guide';
import { Stack, Typography } from '@mui/material';
import { GuideOrder, getGuideOrderDirection } from 'helpers/guideSort';
import DataGridList from 'components/data-grid/DataGridList';
import RowActionButton from 'components/data-grid/RowActionButton';
import StatusBadge from 'components/data-grid/StatusBadge';
import { reportGuideTableTemplate } from '../../template/reportGuideTableTemplate';
import ReportGuideMenu from '../menu/ReportGuideMenu';
import { useReportGuide } from '../../hooks/useReportGuide';

type ReportGuideListTableProps = {
  guides: Guide[];
  handleSort(id: string): void;
  order: GuideOrder | null;
};

const ReportGuideListTable: React.FC<ReportGuideListTableProps> = ({ guides, handleSort, order }) => {
  const { anchorEl, setAnchorEl, setSelectedGuide } = useReportGuide();

  return (
    <>
      {anchorEl && <ReportGuideMenu anchorEl={anchorEl} />}

      <DataGridList
        template={reportGuideTableTemplate}
        rows={guides}
        getRowId={row => row.id}
        rowHeight={56}
        renderCells={{
          action: guide => (
            <RowActionButton
              onOpen={anchor => {
                setAnchorEl(anchor);
                setSelectedGuide(guide);
              }}
            />
          ),
          patientProcedure: guide => (
            <Stack sx={{ minWidth: 0 }}>
              <Typography variant="body2" color="primary" fontWeight={600} noWrap>
                {guide.formattedPatientName}
              </Typography>
              <Typography variant="caption" color="textSecondary" noWrap>
                {guide.formattedProcedureName}
              </Typography>
            </Stack>
          ),
          formattedValueTotal: guide => (
            <Stack sx={{ minWidth: 0, alignItems: 'flex-end' }}>
              <Typography variant="body2" color="primary" fontSize={16} fontWeight={600} noWrap>
                {guide.formattedValueTotal}
              </Typography>
              {guide.value_discount > 0 && (
                <Typography variant="caption" color="error" noWrap>
                  Desc.: {guide.formattedValueDiscount}
                </Typography>
              )}
            </Stack>
          ),
          formattedStatus: guide => <StatusBadge status={guide.status} label={guide.formattedStatus ?? ''} />,
        }}
        exportValues={{
          patientProcedure: guide => `${guide.formattedPatientName} / ${guide.formattedProcedureName}`,
        }}
        serverSort={{ getDirection: originalId => getGuideOrderDirection(originalId, order), onSort: handleSort }}
        fileName="relatorio_guias"
        sheetName="Guias"
      />
    </>
  );
};

export default ReportGuideListTable;
